import { useEffect } from 'react';

import {
  ANNE_ARUNDEL_ORG_DEFAULT_GRADE,
  DEFAULT_GRADE,
} from 'src/constants/grades.constants';
import { ANNE_ARUNDEL_ORG_SOURCED_ID } from 'src/constants/onboarding.constants';
import { useUserGrade } from 'src/hooks/use-user-grade';
import { useUserOrgs } from 'src/hooks/use-user-orgs';
import AuthService from 'src/services/api/auth.service';
import {
  setCurrentGrade,
  setHasViewUpdated,
  setIsGradeDefined,
  useCurrentGradeSelector,
  useSelectedClassSelector,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';
import { calculateUserGradeClassLevel } from 'src/utils/grades.utils';
import { isOrgMember } from 'src/utils/onboarding.utils';

const useCurrentGrade = () => {
  const { userGrade, isGradesLoading } = useUserGrade();
  const { activeOrgs, isLoading: isUserOrgsLoading } = useUserOrgs();

  const selectedClass = useSelectedClassSelector();
  const userCurrentGrade = useCurrentGradeSelector();

  const dispatch = useAppDispatch();

  const isTeacher = AuthService.isTeacher();

  useEffect(() => {
    if (isGradesLoading) {
      return;
    }

    if (!isTeacher && userGrade) {
      dispatch(setCurrentGrade(userGrade));
      dispatch(setIsGradeDefined(true));
    }
  }, [userGrade, isTeacher, isGradesLoading, dispatch]);

  // define teacher user grade for currently selected class
  useEffect(() => {
    if (isGradesLoading || isUserOrgsLoading || !isTeacher) {
      return;
    }

    if (selectedClass) {
      const teacherGrade = calculateUserGradeClassLevel(
        selectedClass,
        activeOrgs,
      );

      dispatch(setCurrentGrade(teacherGrade));
      dispatch(setIsGradeDefined(true));

      return;
    }

    const isAnneArundelOrgMember = isOrgMember(
      ANNE_ARUNDEL_ORG_SOURCED_ID,
      activeOrgs,
    );

    // null indicates that selectedClass was initialized but user does not belong to any classes
    if (selectedClass === null && isAnneArundelOrgMember) {
      dispatch(setCurrentGrade(ANNE_ARUNDEL_ORG_DEFAULT_GRADE));
    }

    if (selectedClass === null && !isAnneArundelOrgMember) {
      dispatch(setCurrentGrade(DEFAULT_GRADE));
    }

    dispatch(setIsGradeDefined(true));
  }, [
    selectedClass,
    isTeacher,
    isGradesLoading,
    dispatch,
    isUserOrgsLoading,
    activeOrgs,
  ]);

  /*
   * View won't be marked as updated in ViewWrapper if grade remains the same
   * and teacher assignments query will return undefined.
   * Marks view as updated if grade remains the same to cover that case.
   * */
  useEffect(() => {
    if (!isTeacher || !selectedClass) return;

    const selectedClassGrade = calculateUserGradeClassLevel(selectedClass);

    if (selectedClassGrade === userCurrentGrade) {
      dispatch(setHasViewUpdated(true));
    }
  }, [selectedClass, userCurrentGrade, isTeacher, dispatch]);

  return { isGradesLoading };
};

export default useCurrentGrade;
